import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Field, Formik, Form } from "formik";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import { Alert, ControlLabel, FormGroup, Col, Input, Row, SegmentedControl } from "components/bootstrap";
import {
  Select,
  Spinner,
  ReadOnlyFormGroup,
  ConfirmDialog,
  FormSubmit,
  FormikInput,
  InputDescription
} from "components/common";
import NumberUtils from "util/NumberUtils";
import DocumentationLink from "components/support/DocumentationLink";
import AppConfig from "util/AppConfig";
import ChecksumTypes from "archive/logic/ChecksumTypes";
import CompressionTypes from "archive/logic/CompressionTypes";
import BackendTypes from "archive/logic/BackendTypes";
import ArchiveConfigStreamSelectionForm from "archive/components/ArchiveConfigStreamSelectionForm";
import useArchiveConfiguration, {
  saveArchiveConfiguration,
  useInvalidateArchiveConfiguration
} from "archive/hooks/useArchiveConfiguration";
import { useArchiveBackends } from "archive/hooks/useArchiveBackends";
import useSnapshotArchivingTypes from "archive/hooks/useSnapshotArchivingTypes";
import { SEGMENT_COMPRESSIONS, SEGMENT_CHECKSUMS } from "archive/types";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import UserNotification from "util/UserNotification";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import useDataTiering from "data-tiering/hooks/useDataTiering";
import useRepositories from "data-tiering/repository-management/hooks/useRepositories";
import SnapshotArchivingFields, {
  DEFAULT_SNAPSHOT_CONFIG,
  validateSnapshotArchivingFields
} from "./snapshot/SnapshotArchivingFields";
import ArchiveRoutes from "../Routes";
const StyledForm = styled(Form)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.md};
  `
);
const StyledAlert = styled(Alert)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.xs};
  `
);
const buildNestedErrors = (backendErrors) => {
  const result = {};
  Object.entries(backendErrors).forEach(([path, msgs]) => {
    var _a;
    const message = msgs.join(" ");
    const dotIndex = path.indexOf(".");
    if (dotIndex === -1) {
      result[path] = message;
    } else {
      const parent = path.slice(0, dotIndex);
      const child = path.slice(dotIndex + 1);
      (_a = result[parent]) != null ? _a : result[parent] = {};
      result[parent][child] = message;
    }
  });
  return result;
};
const validateArchiveConfigForm = (values) => {
  const errors = {};
  if (!values.restrict_to_leader && !(values.parallel_restore_jobs > 0)) {
    errors.parallel_restore_jobs = "At minimum 1 job is required.";
  }
  if (!(values.restore_index_batch_size > 0)) {
    errors.restore_index_batch_size = "Needs to be at least 1.";
  }
  if (!(values.max_segment_size > 0)) {
    errors.max_segment_size = "Needs to be at least 1.";
  }
  if (!(values.retention_time >= 0)) {
    errors.retention_time = "Needs to be at least 0.";
  }
  if (!(values.archive_failure_threshold > 0)) {
    errors.archive_failure_threshold = "Needs to be at least 1.";
  }
  if (!SEGMENT_COMPRESSIONS.includes(values.segment_compression_type)) {
    errors.segment_compression_type = "Please select a valid compression type.";
  }
  if (!SEGMENT_CHECKSUMS.includes(values.segment_checksum_type)) {
    errors.segment_checksum_type = "Please select a valid checksum type.";
  }
  const requiredFields = [
    "backend_id",
    "max_segment_size",
    "restore_index_batch_size",
    "archive_failure_threshold",
    "retention_time"
  ];
  requiredFields.forEach((field) => {
    if (values[field] === "" || typeof values[field] === "undefined") {
      errors[field] = "Is required.";
    }
  });
  return errors;
};
const PARALLEL_RESTORE_JOBS_DEFAULT = 1;
const ArchiveConfigForm = () => {
  var _a, _b, _c;
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [retentionTimeChanged, setRetentionTimeChanged] = useState(false);
  const history = useHistory();
  const sendTelemetry = useSendTelemetry("archive-configuration");
  const invalidateConfiguration = useInvalidateArchiveConfiguration();
  const isCloud = AppConfig.isCloud();
  const isSnapshotEnabled = !isCloud;
  const [userSelectedMode, setUserSelectedMode] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const valuesAtErrorRef = useRef(null);
  const { readiness } = useDataTiering(isSnapshotEnabled);
  const isVersionSupported = (_b = (_a = readiness.data) == null ? void 0 : _a.details.supported_by_search_version) != null ? _b : true;
  const currentUser = useCurrentUser();
  const canEdit = isPermitted(currentUser.permissions, "archiveconfig:update");
  const { repositories: allRepositories } = useRepositories(void 0, { enabled: isSnapshotEnabled });
  const { data: snapshotArchivingTypes } = useSnapshotArchivingTypes();
  const { data: initialArchive } = useArchiveConfiguration();
  const { data: backends } = useArchiveBackends(1, 1e3);
  if (!initialArchive || !backends) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const activeMode = isSnapshotEnabled ? userSelectedMode != null ? userSelectedMode : initialArchive.snapshot_archiving_config != null ? "snapshot" : "default" : "default";
  const supportedRepositoryTypes = (snapshotArchivingTypes != null ? snapshotArchivingTypes : []).map((t) => t.repository_type);
  const repositoryEntries = (_c = allRepositories.data) != null ? _c : [];
  const supportedRepositories = repositoryEntries.filter(
    (entry) => supportedRepositoryTypes.includes(entry.repository.type)
  );
  const credentialProblemRepositories = repositoryEntries.filter((entry) => entry.credential_status === "missing" || entry.credential_status === "invalid").map((entry) => entry.repository.name);
  const checksOutstanding = !allRepositories.checksExhausted && repositoryEntries.some((entry) => entry.status === "unknown" || entry.credential_status === "unknown");
  const snapshotBlocked = (values) => {
    if (!isSnapshotEnabled || isCloud || activeMode !== "snapshot") {
      return false;
    }
    if (allRepositories.isLoading || checksOutstanding) {
      return true;
    }
    const selectedRepository = repositoryEntries.find(
      (entry) => {
        var _a2;
        return entry.repository.name === ((_a2 = values.snapshot_archiving_config) == null ? void 0 : _a2.repository_name);
      }
    );
    return supportedRepositories.length === 0 || credentialProblemRepositories.length > 0 || (selectedRepository == null ? void 0 : selectedRepository.status) === "not_ready";
  };
  const handleRestrictToLeaderChange = (setFieldValue, values, setFieldTouched) => {
    const newValue = !values.restrict_to_leader;
    setFieldValue("restrict_to_leader", newValue);
    if (newValue === true) {
      setFieldValue("parallel_restore_jobs", PARALLEL_RESTORE_JOBS_DEFAULT);
    }
    setFieldTouched("parallel_restore_jobs");
  };
  const updateConfiguration = (values, setErrors, setSubmitting) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.CONFIGURATION_UPDATED, {
      app_pathname: "archive"
    });
    const payload = isSnapshotEnabled && activeMode === "snapshot" ? values : { ...values, snapshot_archiving_config: null };
    saveArchiveConfiguration(payload).then(() => {
      invalidateConfiguration();
      setServerErrors({});
      history.replace(ArchiveRoutes.LIST);
    }).catch((error) => {
      var _a2, _b2;
      const body = (_a2 = error == null ? void 0 : error.additional) == null ? void 0 : _a2.body;
      if ((error == null ? void 0 : error.status) === 409) {
        UserNotification.error(
          "The archive configuration is being modified by another request. Please try again.",
          "Could not update configuration"
        );
      } else if (typeof (body == null ? void 0 : body.errors) === "object") {
        const nestedErrors = buildNestedErrors(body.errors);
        setServerErrors(nestedErrors);
        valuesAtErrorRef.current = values;
        setErrors(nestedErrors);
      } else {
        UserNotification.error(
          `Updating the archive configuration failed: ${(_b2 = body == null ? void 0 : body.message) != null ? _b2 : error}`,
          "Could not update configuration"
        );
      }
      setSubmitting(false);
    });
  };
  const saveConfiguration = (values, {
    setErrors,
    setSubmitting
  }) => {
    if (retentionTimeChanged) {
      setSubmitting(false);
      setShowConfirmDialog(true);
    } else {
      updateConfiguration(values, setErrors, setSubmitting);
    }
  };
  const abortSaving = () => {
    setShowConfirmDialog(false);
  };
  const sortedBackends = backends.backends.map((backend) => ({
    value: backend.id,
    label: `${backend.title} (${BackendTypes.getBackendType(backend.settings.type).label})`
  })).sort((a, b) => naturalSort(a.label.toLowerCase(), b.label.toLowerCase()));
  const retentionTimeString = (retentionTime) => {
    if (retentionTime === void 0 || Number(retentionTime) === 0) {
      return "unlimited";
    }
    return NumberUtils.formatNumber(retentionTime);
  };
  const currentBackend = (configuration) => backends.backends.find((backend) => backend.id === configuration.backend_id);
  const compressionTypesHelpText = /* @__PURE__ */ React.createElement("span", null, "Compression method to apply on archive files. Read the", " ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: "setup-archiving#compression-type", text: "documentation" }), " for more information on the different options.");
  const checksumTypesHelpText = /* @__PURE__ */ React.createElement("span", null, "Checksum method to apply on archive files. Read the", " ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: "setup-archiving#checksum-type", text: "documentation" }), " for more information on the different options.");
  const sortedCompressionTypes = [...CompressionTypes.compressionTypes].sort(
    (t1, t2) => naturalSort(t1.label.toLowerCase(), t2.label.toLowerCase())
  );
  const onCancel = () => history.push(ArchiveRoutes.LIST);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Archives configuration"), /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: initialArchive,
      onSubmit: saveConfiguration,
      validate: (values) => {
        const errors = validateArchiveConfigForm(values);
        if (activeMode === "snapshot") {
          const snapshotErrors = validateSnapshotArchivingFields(values.snapshot_archiving_config);
          if (Object.keys(snapshotErrors).length > 0) {
            errors.snapshot_archiving_config = snapshotErrors;
          }
        }
        const snapshot = valuesAtErrorRef.current;
        if (snapshot) {
          Object.entries(serverErrors).forEach(([key, serverValue]) => {
            if (typeof serverValue === "string") {
              if (values[key] === snapshot[key]) {
                errors[key] = serverValue;
              }
            } else if (typeof serverValue === "object" && serverValue !== null) {
              const groupUnchanged = Object.keys(serverValue).every(
                (childKey) => {
                  var _a2, _b2;
                  return ((_a2 = values[key]) == null ? void 0 : _a2[childKey]) === ((_b2 = snapshot[key]) == null ? void 0 : _b2[childKey]);
                }
              );
              if (groupUnchanged) {
                const existing = errors[key];
                errors[key] = typeof existing === "object" && existing !== null ? { ...existing, ...serverValue } : { ...serverValue };
              }
            }
          });
        }
        return errors;
      },
      validateOnMount: true
    },
    ({ isSubmitting, isValidating, isValid, setFieldValue, values, setFieldTouched, setErrors, setSubmitting }) => {
      var _a2;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledForm, { className: "form form-horizontal" }, !canEdit && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "info" }, "You need the archive configuration update permission (", /* @__PURE__ */ React.createElement("code", null, "archiveconfig:update"), ") to change these settings; the configuration is shown read-only."), /* @__PURE__ */ React.createElement("fieldset", { disabled: !canEdit }, isCloud && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Backend", value: (_a2 = currentBackend(values)) == null ? void 0 : _a2.title }), /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Retention Time (days)", value: initialArchive.retention_time })), !isCloud && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { name: "backend_id" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          name: "backend_id",
          bsStyle: error ? "error" : void 0,
          error,
          label: "Backend",
          required: true,
          help: "In which backend should the archives be stored?",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9"
        },
        /* @__PURE__ */ React.createElement(
          Select,
          {
            placeholder: "Select Backend",
            disabled: !canEdit,
            clearable: false,
            options: sortedBackends,
            onChange: (selected) => onChange({ target: { value: selected, name } }),
            value
          }
        )
      )), isSnapshotEnabled && /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Archiving Strategy"), /* @__PURE__ */ React.createElement("div", { className: "col-sm-9" }, /* @__PURE__ */ React.createElement(
        SegmentedControl,
        {
          data: [
            {
              value: "snapshot",
              label: "Snapshot Strategy (Fast, Harder Setup)",
              disabled: !isVersionSupported
            },
            { value: "default", label: "Scan Strategy (Slow, Easy Setup)" }
          ],
          value: activeMode,
          onChange: (val) => {
            setUserSelectedMode(val);
            setServerErrors({});
            valuesAtErrorRef.current = null;
            setErrors({});
            if (val === "snapshot" && !values.snapshot_archiving_config) {
              setFieldValue("snapshot_archiving_config", DEFAULT_SNAPSHOT_CONFIG);
            }
          }
        }
      ), /* @__PURE__ */ React.createElement(InputDescription, { help: "The Scan strategy archives each index by reading its documents directly from the live index. The Snapshot strategy instead builds the archive from an OpenSearch snapshot of the index, without ever making that snapshot searchable." }), activeMode === "default" && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, "The Scan strategy uses significant disk space in the spooling directory while archives are being written."), !isVersionSupported && /* @__PURE__ */ React.createElement("p", { className: "help-block" }, "Snapshot Archiving requires OpenSearch 2.x or later."))), activeMode === "snapshot" ? /* @__PURE__ */ React.createElement(
        SnapshotArchivingFields,
        {
          repositories: repositoryEntries,
          credentialProblemRepositories,
          hasSupportedRepositories: supportedRepositories.length > 0,
          isLoadingRepositories: allRepositories.isLoading,
          disabled: !canEdit
        }
      ) : /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Enable Multithreading"), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          name: "parallelize_archive_creation",
          id: "parallelize_archive_creation",
          formGroupClassName: "col-sm-9",
          label: "Allow archiving to utilize multiple threads",
          type: "checkbox",
          help: /* @__PURE__ */ React.createElement("span", null, "Enable this option to allow Archiving tasks to use more than one Java thread per Search node.", /* @__PURE__ */ React.createElement("br", null), "This ", /* @__PURE__ */ React.createElement("strong", null, " can increase "), " both the rate of archiving, and", " ", /* @__PURE__ */ React.createElement("strong", null, " load on your Search cluster."), /* @__PURE__ */ React.createElement("p", null), "Note: the maximum number of parallel threads for Archiving is limited to the count of shards in the target Index, and by default is further capped at 50% of search node CPU core count.")
        }
      )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Restrict Restore to Leader Node"), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          name: "restrict_to_leader",
          id: "restrict_to_leader",
          type: "checkbox",
          formGroupClassName: "col-sm-9",
          onChange: () => handleRestrictToLeaderChange(setFieldValue, values, setFieldTouched),
          label: "Restrict archive restore jobs to run only on the leader node",
          help: "Enable this when not all Graylog nodes can reach the backend."
        }
      )), !values.restrict_to_leader && /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "parallel_restore_jobs",
          id: "parallel_restore_jobs",
          label: "Parallel Restore Jobs",
          min: 1,
          help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null, "Number of restore jobs that should run in parallel."), " ", /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, "The option to run Restore Jobs in parallel is currently a ", /* @__PURE__ */ React.createElement("strong", null, "beta feature"), ", and", " ", /* @__PURE__ */ React.createElement("strong", null, "we advise that it should only be used for testing purposes or where explicitly recommended by Graylog Professional Services"), ". Maximum parallelism is limited to the number of Graylog nodes. Higher values may place extraordinary indexing pressure on Opensearch, which risks placing the Opensearch cluster in a red state.")),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "max_segment_size",
          id: "max_segment_size",
          label: "Max Segment Size",
          min: 1,
          help: /* @__PURE__ */ React.createElement("span", null, "Maximum size for each message segment in ", /* @__PURE__ */ React.createElement("strong", null, "bytes"), "."),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      ), /* @__PURE__ */ React.createElement(Field, { name: "segment_compression_type" }, ({ field: { name, value, onChange }, meta: { error } }) => {
        var _a3;
        return /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            error,
            name: "segment_compression_type",
            label: "Compression Type",
            help: compressionTypesHelpText,
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              placeholder: "Select Compression Type",
              disabled: !canEdit,
              options: sortedCompressionTypes,
              onChange: (selected) => onChange({ target: { value: selected, name } }),
              value: (_a3 = CompressionTypes.getCompressionType(value)) == null ? void 0 : _a3.value,
              clearable: false
            }
          )
        );
      }), /* @__PURE__ */ React.createElement(Field, { name: "segment_checksum_type" }, ({ field: { name, value, onChange }, meta: { error } }) => {
        var _a3;
        return /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            error,
            name: "segment_checksum_type",
            label: "Checksum Type",
            help: checksumTypesHelpText,
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              placeholder: "Select Checksum Type",
              disabled: !canEdit,
              options: [...ChecksumTypes.checksumTypes],
              onChange: (selected) => onChange({ target: { value: selected, name } }),
              value: (_a3 = ChecksumTypes.getChecksumType(value)) == null ? void 0 : _a3.value,
              clearable: false
            }
          )
        );
      }), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "restore_index_batch_size",
          id: "restore_index_batch_size",
          label: "Restore index batch size",
          help: "When restoring an archive, what batch size should be used to re-import the data?",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          min: 1,
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "archive_failure_threshold",
          id: "archive_failure_threshold",
          label: "Failure notification threshold",
          help: "Number of consecutive failed attempts before showing an error notification",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          min: 1,
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          min: 0,
          name: "retention_time",
          id: "retention_time",
          label: "Retention Time (days)",
          onChange: ({ target: { value } }) => {
            setRetentionTimeChanged(initialArchive.retention_time !== Number(value));
            setFieldValue("retention_time", value);
          },
          help: "Define how many days to keep archives before deleting them. When 0 is set, automatic deletion is disabled.",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      )), /* @__PURE__ */ React.createElement(Field, { name: "excluded_streams" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          bsStyle: error ? "error" : void 0,
          error,
          name: "excluded_streams",
          label: "Streams to archive",
          help: "Select streams that should be included in the archive. New streams will be archived by default.",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9"
        },
        /* @__PURE__ */ React.createElement(
          ArchiveConfigStreamSelectionForm,
          {
            excludedStreams: value,
            updateExcludedStreams: (selected) => onChange({ target: { value: selected, name } })
          }
        )
      )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { smOffset: 3, sm: 9 }, canEdit && /* @__PURE__ */ React.createElement(
        FormSubmit,
        {
          submitButtonText: "Update configuration",
          disabledSubmit: isValidating || !isValid || snapshotBlocked(values),
          isSubmitting,
          isAsyncSubmit: true,
          submitLoadingText: "Updating configuration...",
          onCancel
        }
      ))))), /* @__PURE__ */ React.createElement(
        ConfirmDialog,
        {
          show: showConfirmDialog,
          onConfirm: () => {
            setShowConfirmDialog(false);
            updateConfiguration(values, setErrors, setSubmitting);
          },
          onCancel: abortSaving,
          title: "Are you sure?"
        },
        /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Archive retention time will be configured to ", retentionTimeString(values == null ? void 0 : values.retention_time), " days. Current configuration is ", retentionTimeString(initialArchive == null ? void 0 : initialArchive.retention_time), " days."), /* @__PURE__ */ React.createElement("p", null, "Deleted archives can't be restored."), /* @__PURE__ */ React.createElement("p", null, "Do you want to continue?"))
      ));
    }
  ));
};
export default ArchiveConfigForm;
